import { motion } from "framer-motion";
import { PhoneMissed, Zap } from "lucide-react";
import { SectionHeading, SectionCta } from "./Reveal";

const LEFT_MSGS = [
  { sys: true, text: "9:02 PM. New inquiry from a Facebook ad. $84." },
  { dir: "in", text: "Hi, do you offer liposuction? What does it start at?" },
  { sys: true, text: "No answer. The front desk left at 6 PM." },
  { sys: true, text: "Next day, 11:14 PM. 26 hours later." },
  { dir: "out", text: "Sorry we missed you. Call us back during business hours." },
  { sys: true, tone: "lost", text: "Patient booked with another practice." },
];

const RIGHT_MSGS = [
  { sys: true, text: "9:02 PM. Same inquiry." },
  { dir: "out", text: "Hi Sarah, this is Mia at [Your Clinic]. Happy to help with that." },
  { dir: "in", text: "Oh wow, fast. Yes, what does lipo start at?" },
  { dir: "out", text: "Great question. It depends on the area, and your consult covers exact pricing. I have Thursday at 2 PM or Friday at 11 AM." },
  { dir: "in", text: "Thursday works." },
  { sys: true, tone: "won", text: "9:15 PM. Booked. Deposit collected." },
];

const Bubble = ({ msg, accent }) => {
  if (msg.sys) {
    const tone =
      msg.tone === "lost" ? "text-red-500" : msg.tone === "won" ? "text-emerald-600" : "text-zinc-500";
    return <p className={`text-center font-mono text-[11px] tracking-wide ${tone} py-1`}>{msg.text}</p>;
  }
  const isOut = msg.dir === "out";
  return (
    <div className={`flex ${isOut ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed rounded-2xl ${
          isOut
            ? accent === "blue"
              ? "bg-champagne text-white rounded-br-sm"
              : "bg-zinc-200 text-zinc-700 rounded-br-sm"
            : "bg-white border border-black/10 text-pearl rounded-bl-sm shadow-sm"
        }`}
      >
        {msg.text}
      </div>
    </div>
  );
};

const Panel = ({ title, sub, icon: Icon, msgs, accent, footer, footerTone, testid }) => (
  <div
    data-testid={testid}
    className={`border bg-carbon overflow-hidden ${
      accent === "blue" ? "border-champagne/40 shadow-[0_20px_60px_rgba(61,165,221,0.12)]" : "border-black/10"
    }`}
  >
    <div className="flex items-center gap-3 border-b border-black/10 bg-white px-6 py-4">
      <span className={`flex h-9 w-9 items-center justify-center rounded-full ${accent === "blue" ? "bg-champagne/10 text-champagne" : "bg-red-500/10 text-red-500"}`}>
        <Icon size={16} />
      </span>
      <div>
        <p className="text-sm font-normal text-pearl">{title}</p>
        <p className="text-xs text-zinc-500">{sub}</p>
      </div>
    </div>
    <div className="space-y-3 px-5 py-6 min-h-[380px]">
      {msgs.map((m, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.5, delay: 0.3 + i * 0.45, ease: [0.16, 1, 0.3, 1] }}
        >
          <Bubble msg={m} accent={accent} />
        </motion.div>
      ))}
    </div>
    <div className={`border-t border-black/10 px-6 py-4 text-center font-mono text-xs tracking-widest uppercase ${footerTone}`}>
      {footer}
    </div>
  </div>
);

export const SpeedToLead = () => (
  <section id="speed-demo" data-testid="speed-demo-section" className="py-24 md:py-36">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <SectionHeading
        eyebrow="Speed to lead"
        title={
          <>
            Same lead. <span className="italic text-champagne">Two very different nights.</span>
          </>
        }
        copy="This is the whole game. The practice that answers first books the patient. Watch both sides play out."
      />

      <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel
          testid="speed-panel-typical"
          title="Typical clinic"
          sub="What happens to most inquiries"
          icon={PhoneMissed}
          msgs={LEFT_MSGS}
          accent="red"
          footer="First response: 26 hours"
          footerTone="text-red-500"
        />
        <Panel
          testid="speed-panel-noble"
          title="With North & Noble"
          sub="The same night, with the Engine installed"
          icon={Zap}
          msgs={RIGHT_MSGS}
          accent="blue"
          footer="First response: 3 minutes"
          footerTone="text-emerald-600"
        />
      </div>

      <SectionCta id="speed-demo" label="I want the right side" />
    </div>
  </section>
);
